import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ChatIcon from "@mui/icons-material/Chat";
import { Approval } from "./approvalsTypes";
import { useClientApprovals } from "../pages/client/useClientApprovals";

interface ApprovalResponseActionsProps {
  approval: Approval;
  projectId: string;
  contractorId: string;
  userRole?: "contractor" | "client";
  userId?: string;
  onStatusChanged?: (newStatus: Approval["status"]) => void;
}

const ApprovalResponseActions: React.FC<ApprovalResponseActionsProps> = ({
  approval,
  projectId,
  contractorId,
  userRole = "contractor",
  userId = "",
  onStatusChanged,
}) => {
  const [actionInProgress, setActionInProgress] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Only clients can respond to approvals (approve/reject)
  const clientHook = useClientApprovals(userId);

  // Only clients can respond when status is pending
  const canRespond = approval.status === "pending" && userRole === "client";

  if (!canRespond) {
    return null;
  }

  // Use client hook for updates
  const updateApprovalStatus = clientHook.updateApprovalStatus;

  const handleApprove = async () => {
    setActionInProgress(true);
    setError(null);

    const success = await updateApprovalStatus(approval.id, "approved");

    if (success) {
      // Wait a moment for state to propagate before callback
      await new Promise((resolve) => setTimeout(resolve, 100));
      onStatusChanged?.("approved");
    } else {
      setError("Failed to approve. Please try again.");
    }

    setActionInProgress(false);
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setActionInProgress(true);
    setError(null);

    const success = await updateApprovalStatus(
      approval.id,
      "rejected",
      rejectionReason.trim(),
    );

    if (success) {
      setRejectDialogOpen(false);
      setRejectionReason("");
      // Wait a moment for state to propagate before callback
      await new Promise((resolve) => setTimeout(resolve, 100));
      onStatusChanged?.("rejected");
    } else {
      setError("Failed to reject. Please try again.");
    }

    setActionInProgress(false);
  };

  const handleDiscussionRequest = async () => {
    setActionInProgress(true);
    setError(null);

    const success = await updateApprovalStatus(approval.id, "discussion");

    if (success) {
      // Wait a moment for state to propagate before callback
      await new Promise((resolve) => setTimeout(resolve, 100));
      onStatusChanged?.("discussion");
    } else {
      setError("Failed to request discussion. Please try again.");
    }

    setActionInProgress(false);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={handleApprove}
          disabled={actionInProgress}
          size="small"
        >
          {actionInProgress ? "Processing..." : "Approve"}
        </Button>

        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={handleRejectClick}
          disabled={actionInProgress}
          size="small"
        >
          Reject
        </Button>

        <Button
          variant="outlined"
          color="info"
          startIcon={<ChatIcon />}
          onClick={handleDiscussionRequest}
          disabled={actionInProgress}
          size="small"
        >
          Discuss
        </Button>
      </Stack>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Approval</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            multiline
            rows={4}
            placeholder="Please explain why you're rejecting this approval..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={actionInProgress}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={actionInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={actionInProgress || !rejectionReason.trim()}
          >
            {actionInProgress ? (
              <CircularProgress size={24} />
            ) : (
              "Confirm Rejection"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalResponseActions;
